import { computed, ref, watch, watchEffect, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { Router } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/store/user'
import { usePermissionStore } from '@/store/permission'
import type { MenuItem } from '@/types/menu'
import type { Menu } from '@/types/menu'

export function convertServerMenusToMenuItems(serverMenus: Menu[], router: Router): MenuItem[] {
  return serverMenus.map(menu => {
    const children = menu.children && menu.children.length > 0
      ? convertServerMenusToMenuItems(menu.children, router)
      : []
    const routePath = menu.routePath
      ? (menu.routePath.startsWith('/') ? menu.routePath : `/${menu.routePath}`)
      : undefined
    const routeRequiresAdmin = routePath
      ? router.resolve(routePath).meta.isAdmin === true
      : false
    const routeRequiresDeviceTypeMaintenance = routePath
      ? router.resolve(routePath).meta.requiresDeviceTypeMaintenance === true
      : false
    const childrenRequireAdmin = children.length > 0
      && children.every(child => child.meta.isAdmin === true)
    const childrenRequireDeviceTypeMaintenance = children.length > 0
      && children.every(child => child.meta.requiresDeviceTypeMaintenance === true)

    return {
      path: routePath || `/${menu.menuCode}`,
      name: menu.menuCode,
      meta: {
        title: menu.menuName,
        icon: menu.icon || 'AppstoreOutlined',
        isAdmin: routeRequiresAdmin || childrenRequireAdmin,
        requiresDeviceTypeMaintenance: routeRequiresDeviceTypeMaintenance
          || childrenRequireDeviceTypeMaintenance,
        hideInMenu: menu.visible === 0,
        permissionCode: menu.permissionCode,
      },
      children,
    }
  })
}

export function filterMenuItemsByAdmin(
  items: MenuItem[],
  isAdmin: boolean,
  canMaintainDeviceTypes = isAdmin,
): MenuItem[] {
  return items.reduce<MenuItem[]>((filtered, item) => {
    if (item.meta.isAdmin && !isAdmin) {
      return filtered
    }
    if (item.meta.requiresDeviceTypeMaintenance && !canMaintainDeviceTypes) {
      return filtered
    }

    const hadChildren = Boolean(item.children?.length)
    const children = hadChildren
      ? filterMenuItemsByAdmin(item.children || [], isAdmin, canMaintainDeviceTypes)
      : item.children
    if (hadChildren && children?.length === 0) {
      return filtered
    }

    filtered.push({ ...item, children })
    return filtered
  }, [])
}

/** 后端菜单加载完成后以服务端结果为准，避免空树回退到本地全量路由。 */
export function selectMenuItems(
  serverItems: MenuItem[],
  localItems: MenuItem[],
  menuLoaded: boolean,
  isAdmin: boolean,
  canMaintainDeviceTypes = isAdmin,
): MenuItem[] {
  return filterMenuItemsByAdmin(
    menuLoaded ? serverItems : localItems,
    isAdmin,
    canMaintainDeviceTypes,
  )
}

/**
 * 菜单管理 Composable
 * 处理侧边栏菜单的展开、选中、权限过滤等逻辑
 */
export function useMenu() {
  const route = useRoute()
  const router = useRouter()
  const userStore = useUserStore()
  const permissionStore = usePermissionStore()
  useI18n()

  // 展开的菜单keys（默认展开机器人管理）
  const openKeys = ref<string[]>(['router.parent.robotManagement'])

  // 根级子菜单keys（用于手风琴模式和排序）
  // 注：任务中心/养成中心/内容中心/会员中心已移入泡芙产品，插件市场已移入创意工坊
  const rootSubmenuKeys = [
    'router.parent.robotManagement',
    'router.parent.deviceManagement', // 智能套件 (原IoT管理)
    'router.parent.paofuManagement', // 泡芙产品（含任务/养成/内容/会员子分组）
    'router.parent.mallCenter', // 商城中心
    'router.parent.marketingCenter', // 营销中心
    'router.parent.accountCenter', // 统一账号中心
    'router.parent.financeCenter', // 财务中心
    'router.parent.dataCenter', // 数据中心
    'router.parent.serviceCenter', // 客服中心
    'router.parent.monitorOps', // 监控运维
    'router.parent.systemSecurity', // 系统安全
    'router.parent.systemSettings',
    // 'router.parent.roleManagement', // 音色市场已并入智能方案
    'router.parent.creativeWorkshop', // 创意工坊（含插件市场子分组）
  ]

  // Parent Menu Icon Mapping
  const parentIcons: Record<string, string> = {
    'router.parent.robotManagement': 'AppstoreOutlined', // 智能服务
    'router.parent.deviceManagement': 'MobileOutlined', // 智能套件
    'router.parent.monitorOps': 'MonitorOutlined', // 监控运维
    'router.parent.systemSecurity': 'SafetyCertificateOutlined', // 系统安全
    'router.parent.magicToolMarket': 'ToolOutlined',
    'router.parent.creativeWorkshop': 'BulbOutlined',
    'router.parent.systemSettings': 'SettingOutlined',
    'router.parent.knowledgeManagement': 'BookOutlined',
    'router.parent.solutionManagement': 'SolutionOutlined', // 解决方案管理
    'router.parent.workflowManagement': 'NodeIndexOutlined',
    'router.parent.modelManagement': 'ApiOutlined',
    'router.parent.roleManagement': 'UserSwitchOutlined',
    'router.parent.dataManagement': 'DatabaseOutlined',
    // New Icons
    'router.parent.magicAbilityMarket': 'ThunderboltOutlined',
    'router.parent.solutionMarket': 'SolutionOutlined',
    'router.parent.dataMarket': 'BarChartOutlined',
    'router.parent.deviceMarketInternal': 'ShopOutlined', // 内部设备市场
    // 'router.parent.voiceMarket': 'AudioOutlined', // 音色市场（已并入智能方案）
    // 泡芙产品
    'router.parent.paofuManagement': 'GiftOutlined',
    // 商城中心
    'router.parent.mallCenter': 'ShoppingCartOutlined',
    // 营销中心
    'router.parent.marketingCenter': 'RiseOutlined',
    // 统一账号中心
    'router.parent.accountCenter': 'IdcardOutlined',
    // 财务中心
    'router.parent.financeCenter': 'DollarOutlined',

    // 会员中心
    'router.parent.memberCenter': 'CrownOutlined',
    // 数据中心
    'router.parent.dataCenter': 'DatabaseOutlined',
    // 客服中心
    'router.parent.serviceCenter': 'CustomerServiceOutlined',
  }

  // 虚拟父节点的层级关系配置 (Child -> Parent)
  // 如果一个虚拟父节点（如 knowledgeManagement）本身也需要归属于另一个父节点（如 robotManagement），在这里配置
  const virtualNodeParents: Record<string, string> = {
    'router.parent.knowledgeManagement': 'router.parent.robotManagement', // 1. 知识图谱 -> 智能服务
    'router.parent.solutionManagement': 'router.parent.robotManagement', // 智能方案
    // 'router.parent.modelManagement' - 已废弃，不再挂载到智能服务下

    // 1. 设备管理 (现在是顶级菜单，不需要挂载到 robotManagement)
    // 'router.parent.deviceManagement': 'router.parent.robotManagement',

    // 1.1 固件管理 -> 设备管理
    'router.parent.firmwareManagement': 'router.parent.deviceManagement',

    // 1.2 数据中心 -> 设备管理
    'router.parent.dataManagement': 'router.parent.deviceManagement',


    // 4. 创意工坊子菜单
    'router.parent.magicAbilityMarket': 'router.parent.creativeWorkshop',
    'router.parent.solutionMarket': 'router.parent.creativeWorkshop',
    'router.parent.dataMarket': 'router.parent.creativeWorkshop',
    'router.parent.deviceMarketInternal': 'router.parent.creativeWorkshop',

    // 6. 泡芙产品子分组（原一级菜单下沉）
    // 注：任务管理/养成管理/内容中心 仅有单个子菜单，已直接挂 paofuManagement，无需虚拟父节点
    'router.parent.memberCenter': 'router.parent.paofuManagement',

    // 2. 音色市场(roleManagement/voiceMarket) -> 已并入智能方案，不需独立配置
    // 'router.parent.roleManagement': 'router.parent.creativeWorkshop',
  }


  // 获取所有菜单项（从路由配置中获取）
  const menuItems = computed<MenuItem[]>(() => {
    // 找到主布局路由
    const mainRoute = router.getRoutes().find(r => r.path === '/' && r.children)
    if (!mainRoute?.children) return []

    // 菜单映射，用于组织父子关系 (key -> MenuItem)
    const menuMap = new Map<string, MenuItem>()
    const rootMenus: MenuItem[] = []

    // 1. 先将所有真实路由节点放入 Map
    mainRoute.children
      .filter(route => route.meta?.title && !route.meta?.hideInMenu)
      .forEach(route => {
        const menuItem: MenuItem = {
          path: `/${route.path}`,
          name: route.name as string,
          meta: route.meta as any,
          children: []
        }
        // 使用 path 或 name 作为 key
        // 优先使用 name，如果没有 name 则使用 path
        // 但为了与 parent 匹配，我们需要一种一致的 key 策略
        // 路由 meta.parent 指向的是字符串 key (可以是 path 也可以是 virtual key)
        menuMap.set(menuItem.path, menuItem)
        // 同时存一份 name 映射（如果 parent 指向的是 name）
        if (menuItem.name) {
          menuMap.set(menuItem.name, menuItem)
        }
      })

    // 2. 辅助函数：获取或创建父节点
    const getOrCreateParent = (parentKey: string): MenuItem => {
      if (menuMap.has(parentKey)) {
        return menuMap.get(parentKey)!
      }

      // 如果不存在，创建一个虚拟父节点
      const newParent: MenuItem = {
        path: parentKey, // 虚拟节点的 path 就是它的 key
        name: parentKey,
        meta: {
          title: parentKey,
          icon: parentIcons[parentKey] || 'AppstoreOutlined',
          isAdmin: false // 默认 false，后续可以根据子节点推断，或者显式配置
        },
        children: []
      }
      menuMap.set(parentKey, newParent)

      // 递归检查这个新创建的虚拟节点是否有父节点 (GrandParent)
      if (virtualNodeParents[parentKey]) {
        const grandParentKey = virtualNodeParents[parentKey]
        const grandParent = getOrCreateParent(grandParentKey)
        if (!grandParent.children) grandParent.children = []
        // 避免重复添加
        if (!grandParent.children.some(child => child.path === newParent.path)) {
          grandParent.children.push(newParent)
        }
      } else {
        // 如果没有父节点，那它就是根节点
        rootMenus.push(newParent)
      }

      return newParent
    }

    // 3. 再次遍历真实路由，将它们挂载到正确的父节点
    mainRoute.children
      .filter(route => route.meta?.title && !route.meta?.hideInMenu)
      .forEach(route => {
        // 重新构建 menuItem 对象（因为上面只是为了存 Map，现在要真正挂载）
        // 其实可以直接从 Map 取，但要注意不要重复处理
        // 这里的逻辑是：每个真实路由节点，如果有 parent，就挂载；如果没有，就放到 rootMenus

        const currentKey = `/${route.path}`
        const menuItem = menuMap.get(currentKey)!

        if (route.meta?.parent) {
          const parentKey = route.meta.parent as string
          const parentMenu = getOrCreateParent(parentKey)

          if (!parentMenu.children) parentMenu.children = []
          // 检查是否已经添加过（因为 map.set 会覆盖，但 object reference 是同一个）
          if (!parentMenu.children.some(child => child.path === menuItem.path)) {
            parentMenu.children.push(menuItem)
          }
        } else {
          // 如果没有 parent，且之前没有被作为 root 添加过
          if (!rootMenus.some(root => root.path === menuItem.path)) {
            rootMenus.push(menuItem)
          }
        }
      })

    // 4. 对根菜单进行排序
    rootMenus.sort((a, b) => {
      const indexA = rootSubmenuKeys.indexOf(a.path);
      const indexB = rootSubmenuKeys.indexOf(b.path);

      // 如果都在列表中，按列表顺序排序
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;

      // 如果只有A在列表中，A排在后面（让未定义的如Dashboard排在前面）
      if (indexA !== -1) return 1;

      // 如果只有B在列表中，B排在后面
      if (indexB !== -1) return -1;

      // 如果都不在列表中，保持原序
      return 0;
    });

    return rootMenus
  })

  /**
   * 从后端获取的菜单树转换后的 MenuItem
   */
  const serverMenuItems = computed<MenuItem[]>(() => {
    if (permissionStore.userMenuTree.length === 0) {
      return []
    }
    return convertServerMenusToMenuItems(permissionStore.userMenuTree, router)
  })

  // 是否是管理员
  const isAdmin = computed(() => {
    return String(userStore.userInfo?.isAdmin) === '1'
  })
  const canMaintainDeviceTypes = computed(() => permissionStore.canMaintainDeviceTypes)

  // 过滤后的菜单（根据权限）
  // 优先使用后端菜单树，如果为空则 fallback 到路由元数据方式
  const filteredMenuItems = computed(() => {
    return selectMenuItems(
      serverMenuItems.value,
      menuItems.value,
      permissionStore.menuLoaded,
      isAdmin.value,
      canMaintainDeviceTypes.value,
    )
  })

  // 当前选中的菜单key
  const selectedKeys = ref<string[]>([route.path])

  // 自动同步路由到选中菜单
  watchEffect(() => {
    selectedKeys.value = [route.path]
  })

  /**
   * 处理菜单展开变化（手风琴模式）
   */
  function handleOpenChange(keys: string[]) {
    const latestOpenKey = keys.find(key => !openKeys.value.includes(key))

    if (latestOpenKey && rootSubmenuKeys.includes(latestOpenKey)) {
      // 如果打开的是根级菜单，只保留这一个
      openKeys.value = [latestOpenKey]
    } else {
      // 否则保留所有打开的菜单
      openKeys.value = keys
    }
  }

  /**
   * 菜单点击处理
   */
  function handleMenuClick(path: string) {
    router.push(path)
  }

  function findServerParentKeys(items: MenuItem[], targetPath: string, parents: string[] = []): string[] {
    for (const item of items) {
      if (item.path === targetPath) {
        return parents
      }
      if (item.children && item.children.length > 0) {
        const matchedParents = findServerParentKeys(item.children, targetPath, [...parents, item.path])
        if (matchedParents.length > 0) {
          return matchedParents
        }
      }
    }
    return []
  }

  // 监听路由变化，自动展开对应的菜单
  watch(
    () => [route.path, serverMenuItems.value] as const,
    () => {
      const currentRoute = router.currentRoute.value
      if (currentRoute.meta?.parent) {
        const parentKey = currentRoute.meta.parent as string
        const keysToOpen = new Set(openKeys.value)

        // 递归查找所有父级并展开
        let currentParentKey = parentKey
        while (currentParentKey) {
          keysToOpen.add(currentParentKey)
          currentParentKey = virtualNodeParents[currentParentKey] || ''
        }

        findServerParentKeys(serverMenuItems.value, route.path).forEach(key => keysToOpen.add(key))

        openKeys.value = Array.from(keysToOpen)
      }
    },
    { immediate: true }
  )

  /**
   * 刷新后端菜单树
   */
  async function refreshServerMenu() {
    await permissionStore.fetchUserMenuTree()
  }

  // 组件挂载时尝试加载后端菜单（如果还未加载）
  onMounted(() => {
    const hasLogin = Boolean(userStore.token || userStore.userInfo)
    const needsPermissionData = !permissionStore.permissionDataLoaded
      || !permissionStore.menuLoaded
    if (hasLogin && needsPermissionData) {
      permissionStore.initPermissionData()
    }
  })

  return {
    openKeys,
    selectedKeys,
    menuItems: filteredMenuItems,
    serverMenuItems,
    isAdmin,
    canMaintainDeviceTypes,
    menuLoading: computed(() => permissionStore.menuLoading),
    menuLoaded: computed(() => permissionStore.menuLoaded),
    handleOpenChange,
    handleMenuClick,
    refreshServerMenu,
  }
}
